import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

const SettingsTab = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'businessman';

  // Outer tab: 'categories' | 'crops' | 'locations'
  const [mainTab, setMainTab] = useState('categories');
  // Inside Categories: 'expense_category' or 'income_category'
  const [categoryType, setCategoryType] = useState('expense_category');

  const [lists, setLists] = useState({
    expense_category: [],
    income_category: [],
    crop_name: [],
    crop_type: [],
    location: []
  });
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state (for adding categories, crops, types, locations)
  const [showForm, setShowForm] = useState(false);
  const [modalContext, setModalContext] = useState(null); // { type, parentId, parentName }
  const [editId, setEditId] = useState(null);
  const [formValue, setFormValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Assign-land modal state
  const [assignModal, setAssignModal] = useState(null); // { locationId, locationName }
  const [assignLandId, setAssignLandId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [exp, inc, crops, cropTypes, locations, landsRes] = await Promise.all([
        axios.get(`${API}/agriculture/settings/expense_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/income_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/crop_name`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/crop_type`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/location`, { headers: headers() }),
        axios.get(`${API}/agriculture/lands-with-location`, { headers: headers() })
      ]);
      setLists({
        expense_category: exp.data.data || [],
        income_category: inc.data.data || [],
        crop_name: crops.data.data || [],
        crop_type: cropTypes.data.data || [],
        location: locations.data.data || []
      });
      setLands(landsRes.data.data || []);
    } catch (err) {
      console.error('Failed to load settings', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Add/Edit modal openers ──
  const openAddCategory = () => {
    setModalContext({ type: categoryType, parentId: null, parentName: null });
    setEditId(null); setFormValue(''); setError(''); setShowForm(true);
  };
  const openAddCrop = () => {
    setModalContext({ type: 'crop_name', parentId: null, parentName: null });
    setEditId(null); setFormValue(''); setError(''); setShowForm(true);
  };
  const openAddType = (crop) => {
    setModalContext({ type: 'crop_type', parentId: crop.id, parentName: crop.value });
    setEditId(null); setFormValue(''); setError(''); setShowForm(true);
  };
  const openAddLocation = () => {
    setModalContext({ type: 'location', parentId: null, parentName: null });
    setEditId(null); setFormValue(''); setError(''); setShowForm(true);
  };
  const openEdit = (item) => {
    setModalContext({ type: item.setting_type, parentId: item.parent_id, parentName: null });
    setEditId(item.id); setFormValue(item.value); setError(''); setShowForm(true);
  };

  const handleSave = async () => {
    const trimmed = formValue.trim();
    if (!trimmed) { setError('Name cannot be empty'); return; }
    if (trimmed.length > 100) { setError('Name must be 100 characters or less'); return; }

    setSaving(true); setError('');
    try {
      if (editId) {
        await axios.put(`${API}/agriculture/settings/${editId}`, { value: trimmed }, { headers: headers() });
      } else {
        const payload = { setting_type: modalContext.type, value: trimmed };
        if (modalContext.type === 'crop_type') payload.parent_id = modalContext.parentId;
        await axios.post(`${API}/agriculture/settings`, payload, { headers: headers() });
      }
      setShowForm(false); fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (item) => {
    let warning = '';
    if (item.setting_type === 'crop_name') warning = `\n\n⚠️ This will also delete all crop types under "${item.value}".`;
    if (item.setting_type === 'location') warning = `\n\n⚠️ Lands in this group will become unassigned (not deleted).`;
    if (!window.confirm(`Delete "${item.value}"?${warning}`)) return;
    try {
      await axios.delete(`${API}/agriculture/settings/${item.id}`, { headers: headers() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  // ── Assign land to location ──
  const openAssign = (location) => {
    setAssignModal({ locationId: location.id, locationName: location.value });
    setAssignLandId('');
  };
  const handleAssign = async () => {
    if (!assignLandId) return;
    setAssigning(true);
    try {
      await axios.post(`${API}/agriculture/lands/assign-location`, {
        land_id: assignLandId,
        location_id: assignModal.locationId
      }, { headers: headers() });
      setAssignModal(null);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign land');
    }
    setAssigning(false);
  };
  const handleUnassign = async (land) => {
    if (!window.confirm(`Remove "${land.field_name}" from this group?`)) return;
    try {
      await axios.post(`${API}/agriculture/lands/assign-location`, {
        land_id: land.id,
        location_id: null
      }, { headers: headers() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove land');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading settings...</div>;

  const categoryList = lists[categoryType];
  const categoryColor = categoryType === 'expense_category' ? '#e74c3c' : '#27ae60';

  const typesByCrop = (() => {
    const map = {};
    lists.crop_type.forEach(t => {
      if (!map[t.parent_id]) map[t.parent_id] = [];
      map[t.parent_id].push(t);
    });
    return map;
  })();

  // Lands grouped by location_id
  const landsByLocation = (() => {
    const map = {};
    lands.forEach(l => {
      if (l.location_id) {
        if (!map[l.location_id]) map[l.location_id] = [];
        map[l.location_id].push(l);
      }
    });
    return map;
  })();

  // Unassigned lands (for the assign picker)
  const unassignedLands = lands.filter(l => !l.location_id);

  const modalTitle = (() => {
    if (!modalContext) return '';
    const action = editId ? 'Edit' : 'Add';
    const labels = {
      expense_category: 'Expense Category',
      income_category: 'Income Category',
      crop_name: 'Crop',
      crop_type: `Crop Type${modalContext.parentName ? ` for ${modalContext.parentName}` : ''}`,
      location: 'Location Group'
    };
    return `${action} ${labels[modalContext.type]}`;
  })();

  const modalPlaceholder = (() => {
    if (!modalContext) return '';
    return {
      expense_category: 'e.g. Diesel, Tractor Rent',
      income_category: 'e.g. Subsidy, Grant',
      crop_name: 'e.g. Wheat, Sugarcane',
      crop_type: 'e.g. Barani, Hybrid',
      location: 'e.g. North Group, Millat Road'
    }[modalContext.type];
  })();

  // Top-right add button action + label
  const addButton = (() => {
    if (mainTab === 'categories') return { fn: openAddCategory, label: `+ Add ${categoryType === 'expense_category' ? 'Expense' : 'Income'} Category`, color: categoryColor };
    if (mainTab === 'crops') return { fn: openAddCrop, label: '+ Add Crop', color: '#16a085' };
    return { fn: openAddLocation, label: '+ Add Location', color: '#0F6E56' };
  })();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>⚙️ Settings</h2>
          <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px' }}>
            Manage dropdown options and land groups
          </div>
        </div>
        {isOwner && (
          <button onClick={addButton.fn} style={{ padding: '12px 24px', background: addButton.color, color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>
            {addButton.label}
          </button>
        )}
      </div>

      {!isOwner && (
        <div style={{ padding: '14px 18px', background: '#fff3cd', borderRadius: '10px', borderLeft: '4px solid #f39c12', fontSize: '13px', color: '#7f5b00', marginBottom: '20px' }}>
          🔒 You are logged in as <strong>{user?.role}</strong>. Only the owner can add, edit, or delete settings.
        </div>
      )}

      {/* Main tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={() => setMainTab('categories')} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: mainTab === 'categories' ? '#3498db' : '#f0f0f0', color: mainTab === 'categories' ? 'white' : '#555' }}>
          💰 Categories ({lists.expense_category.length + lists.income_category.length})
        </button>
        <button onClick={() => setMainTab('crops')} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: mainTab === 'crops' ? '#16a085' : '#f0f0f0', color: mainTab === 'crops' ? 'white' : '#555' }}>
          🌾 Crops ({lists.crop_name.length})
        </button>
        <button onClick={() => setMainTab('locations')} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: mainTab === 'locations' ? '#0F6E56' : '#f0f0f0', color: mainTab === 'locations' ? 'white' : '#555' }}>
          📍 Locations ({lists.location.length})
        </button>
      </div>

      {/* ─── CATEGORIES TAB ─── */}
      {mainTab === 'categories' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button onClick={() => setCategoryType('expense_category')} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px', background: categoryType === 'expense_category' ? '#e74c3c' : '#f0f0f0', color: categoryType === 'expense_category' ? 'white' : '#555' }}>
              📉 Expense ({lists.expense_category.length})
            </button>
            <button onClick={() => setCategoryType('income_category')} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px', background: categoryType === 'income_category' ? '#27ae60' : '#f0f0f0', color: categoryType === 'income_category' ? 'white' : '#555' }}>
              💰 Income ({lists.income_category.length})
            </button>
          </div>
          <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: categoryColor, margin: 0 }}>
                {categoryType === 'expense_category' ? '📉 Expense Categories' : '💰 Income Categories'}
              </h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  {['#', 'Name', 'Order', isOwner ? 'Actions' : null].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '1px solid #eee' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categoryList.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No entries yet. Click "+ Add" to create one.</td></tr>
                ) : categoryList.map((item, i) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', color: '#7f8c8d' }}>{i + 1}</td>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2c3e50' }}>{item.value}</td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{item.display_order}</td>
                    {isOwner && (
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => openEdit(item)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                          <button onClick={() => handleDelete(item)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── CROPS TAB ─── */}
      {mainTab === 'crops' && (
        <div>
          {lists.crop_name.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
              No crops yet. Click "+ Add Crop" to create your first crop.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {lists.crop_name.map(crop => {
                const types = typesByCrop[crop.id] || [];
                return (
                  <div key={crop.id} style={{ background: 'white', borderRadius: '14px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 18px', background: 'linear-gradient(135deg, #16a085 0%, #1abc9c 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>🌾 {crop.value}</div>
                      {isOwner && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEdit(crop)} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                          <button onClick={() => handleDelete(crop)} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '14px 18px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#95a5a6', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Crop Types ({types.length})
                      </div>
                      {types.length === 0 ? (
                        <div style={{ fontSize: '13px', color: '#bbb', fontStyle: 'italic', marginBottom: '12px' }}>No types added yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          {types.map(t => (
                            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px' }}>
                              <span style={{ fontSize: '14px', color: '#2c3e50', fontWeight: '500' }}>🏷️ {t.value}</span>
                              {isOwner && (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button onClick={() => openEdit(t)} style={{ padding: '3px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                                  <button onClick={() => handleDelete(t)} style={{ padding: '3px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {isOwner && (
                        <button onClick={() => openAddType(crop)} style={{ width: '100%', padding: '10px', background: '#e8f6f3', color: '#16a085', border: '1px dashed #16a085', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                          + Add Type
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── LOCATIONS TAB ─── */}
      {mainTab === 'locations' && (
        <div>
          {lists.location.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
              No location groups yet. Click "+ Add Location" to create your first group.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {lists.location.map(loc => {
                const groupLands = landsByLocation[loc.id] || [];
                const totalKanals = groupLands.reduce((s, l) => s + parseInt(l.total_kanals || 0), 0);
                return (
                  <div key={loc.id} style={{ background: 'white', borderRadius: '14px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 18px', background: '#0F6E56', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>📍 {loc.value}</div>
                      {isOwner && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEdit(loc)} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                          <button onClick={() => handleDelete(loc)} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '14px 18px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#95a5a6', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Lands in group ({groupLands.length}) — {totalKanals} kanals
                      </div>
                      {groupLands.length === 0 ? (
                        <div style={{ fontSize: '13px', color: '#bbb', fontStyle: 'italic', marginBottom: '12px' }}>No lands assigned yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          {groupLands.map(l => (
                            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px' }}>
                              <span style={{ fontSize: '14px', color: '#2c3e50', fontWeight: '500' }}>🗺️ {l.field_name}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#7f8c8d' }}>{l.total_kanals} kanals</span>
                                {isOwner && (
                                  <button onClick={() => handleUnassign(l)} style={{ padding: '2px 8px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}>Remove</button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {isOwner && (
                        <button onClick={() => openAssign(loc)} style={{ width: '100%', padding: '10px', background: '#E1F5EE', color: '#0F6E56', border: '1px dashed #0F6E56', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                          + Assign Land
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: '20px', padding: '14px 18px', background: '#e8f4fd', borderRadius: '10px', borderLeft: '4px solid #3498db', fontSize: '13px', color: '#1a5490' }}>
            💡 <strong>How it works:</strong> Group expenses (like a shared water pump) will split across all lands in the group by their kanal area, then down to each land's crops.
          </div>
        </div>
      )}

      {/* Info note (categories/crops only) */}
      {mainTab !== 'locations' && (
        <div style={{ marginTop: '20px', padding: '14px 18px', background: '#e8f4fd', borderRadius: '10px', borderLeft: '4px solid #3498db', fontSize: '13px', color: '#1a5490' }}>
          💡 <strong>Note:</strong> Expense/Income categories used in existing transactions cannot be deleted. Crop names and types can be deleted freely — old crop records keep their existing names unchanged.
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && modalContext && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>{modalTitle}</h3>
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '20px' }}>
              {editId ? 'Update the name below.' : 'This will be added to the list.'}
            </div>
            <div>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} value={formValue} onChange={e => setFormValue(e.target.value)} placeholder={modalPlaceholder} autoFocus maxLength={100} onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }} />
            </div>
            {error && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fdecea', borderRadius: '8px', color: '#c0392b', fontSize: '13px' }}>❌ {error}</div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#16a085', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Land Modal */}
      {assignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>Assign Land to {assignModal.locationName}</h3>
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '20px' }}>
              Only lands not already in a group are shown.
            </div>
            {unassignedLands.length === 0 ? (
              <div style={{ padding: '14px 18px', background: '#fff3cd', borderRadius: '8px', fontSize: '13px', color: '#7f5b00' }}>
                All lands are already assigned to groups. Remove a land from its current group first.
              </div>
            ) : (
              <div>
                <label style={labelStyle}>Select Land *</label>
                <select style={inputStyle} value={assignLandId} onChange={e => setAssignLandId(e.target.value)} autoFocus>
                  <option value="">-- Select Land --</option>
                  {unassignedLands.map(l => (
                    <option key={l.id} value={l.id}>{l.field_name} ({l.total_kanals} kanals)</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAssignModal(null)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              {unassignedLands.length > 0 && (
                <button onClick={handleAssign} disabled={assigning || !assignLandId} style={{ padding: '12px 24px', background: '#0F6E56', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', opacity: assignLandId ? 1 : 0.5 }}>
                  {assigning ? 'Assigning...' : 'Assign'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;